<template>
    <div class="absolute right-3 bottom-32 flex flex-col items-center gap-6 z-[49]">
        <div v-if="videoData.is_audition">
            <!-- Released Status -->
            <a
                v-if="isReleased"
                @click.stop="$emit('watchReleasedSceneClick')"
                :href="route(routePrefix + '.scene.show', videoData.scene_slug)"
                target="_blank"
                class="flex flex-col items-center gap-2"
                type="button"
                aria-label="Watch full scene"
            >
                <div class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-xl">
                    <Play class="w-6 h-6 text-white ml-0.5" fill="white" />
                </div>
                <div class="text-center">
                <span class="text-emerald-400 text-xs font-bold block">
                    Watch Scene
                </span>
                    <span class="text-white/60 text-[10px]">
                    {{ videoData.likes }} stars
                </span>
                </div>
            </a>

            <!-- Production Status -->
            <div v-else-if="isProduction" class="flex flex-col items-center gap-2">
                <div class="relative w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-xl">
                    <Film class="w-7 h-7 text-white" :stroke-width="2.5" />
                    <div class="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-20" />
                </div>
                <span class="text-white/60 text-[10px]">
                {{ videoData.likes }} stars
            </span>
            </div>

            <!-- Active Status (new/almost/sponsored) -->
            <button
                v-else
                @click="$emit('starClick')"
                :aria-label="starAriaLabel"
                class="flex flex-col items-center gap-2 group"
                type="button"
            >
                <div class="relative w-14 h-14">
                    <!-- User involvement glow - Brand blue -->
                    <div
                        v-if="showSponsoredIndicators"
                        class="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full blur-md opacity-40 animate-pulse"
                    />

                    <!-- Sponsored success glow - Deep Blue -->
                    <div
                        v-if="isFunded && !showSponsoredIndicators"
                        class="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-600 rounded-full blur-md opacity-35"
                    />

                    <svg
                        class="absolute inset-0 w-full h-full -rotate-90"
                        viewBox="0 0 64 64"
                        aria-hidden="true"
                    >
                        <circle
                            cx="32"
                            cy="32"
                            :r="radius"
                            fill="none"
                            :stroke="ringBgStroke"
                            stroke-width="4"
                        />
                        <circle
                            cx="32"
                            cy="32"
                            :r="radius"
                            fill="none"
                            :stroke="progressStroke"
                            stroke-width="4"
                            :stroke-dasharray="circumference"
                            :stroke-dashoffset="strokeDashoffset"
                            stroke-linecap="round"
                            class="transition-all duration-500"
                        />
                    </svg>

                    <div class="absolute inset-0 flex items-center justify-center">
                        <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                    </div>

                    <!-- User's sponsorship badge - Brand blue -->
                    <div
                        v-if="showSponsoredIndicators"
                        class="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center shadow-lg border border-black/40"
                    >
                        <img :src="STAR_ICON" alt="" class="w-3 h-3" />
                    </div>

                    <!-- Sponsored goal reached badge - Sky Blue (celebration) -->
                    <div
                        v-if="isFunded"
                        class="absolute -top-1 -left-1 w-5 h-5 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center shadow-lg border border-black/40"
                    >
                        <Check class="w-3 h-3 text-white" :stroke-width="3" />
                    </div>
                </div>

                <div class="text-center">
                    <!-- User sponsored AND goal reached -->
                    <template v-if="showSponsoredIndicators && isFunded">
                    <span class="text-white text-xs font-bold block">
                        Sponsored!
                    </span>
                        <span class="text-white text-[11px] inline-flex items-center gap-1 justify-center">
                        You added {{ videoData.stars_given_by_user }}
                        <img :src="STAR_ICON" alt="" class="w-3.5 h-3.5" />
                    </span>

                        <span class="text-white/70 text-[10px] block">
                        {{ videoData.likes }} stars total
                    </span>
                    </template>

                    <!-- User sponsored but goal not reached yet -->
                    <template v-else-if="showSponsoredIndicators">
                    <span class="text-white text-xs font-bold inline-flex items-center gap-1 justify-center">
                        You added {{ videoData.stars_given_by_user }}
                        <img :src="STAR_ICON" alt="" class="w-3.5 h-3.5" />
                    </span>

                        <span class="text-white/70 text-[10px] block">
                        {{ videoData.likes }} / {{ videoData.star_goal }} stars
                    </span>

                        <span class="text-white/85 text-[10px] font-semibold inline-flex items-center mt-0.5 group-hover:text-white transition">
                        Give more
                    </span>
                    </template>

                    <!-- Sponsored but user didn't contribute -->
                    <template v-else-if="isFunded">
                    <span class="text-white text-xs font-bold block">
                        Sponsored!
                    </span>
                        <span class="text-white/70 text-[10px] block">
                        {{ videoData.likes }} stars total
                    </span>
                    </template>

                    <!-- Normal state - goal not reached -->
                    <template v-else>
                    <span class="text-white text-xs font-semibold">
                        {{ videoData.likes }} stars out of {{ videoData.star_goal }}
                    </span>
                        <span class="text-white/80 text-[12px] block">
                        towards the goal
                    </span>
                    </template>
                </div>
            </button>
        </div>

        <!-- Creator avatar -->
        <button class="relative group" type="button" aria-label="View creator">
            <div class="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg bg-zinc-800">
                <img
                    v-if="videoData.creator.avatar"
                    :src="videoData.creator.avatar"
                    :alt="videoData.creator.name"
                    class="w-full h-full object-cover"
                    @error="handleImageError"
                />
                <div v-else class="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                    {{ creatorInitial }}
                </div>
            </div>
        </button>

        <!-- Share button -->
        <button
            data-modal-target="ShareLinkModal"
            data-modal-toggle="ShareLinkModal"
            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
            type="button"
            aria-label="Share"
        >
            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z" />
                </svg>
            </div>
            <span class="text-white/60 text-[12px] font-medium tracking-wide">Share</span>
        </button>
    </div>

    <SharePopup />
</template>

<script setup>
import { computed } from 'vue'
import { Check, Film, Play } from 'lucide-vue-next'
import SharePopup from "@/Components/Common/SharePopup.vue";

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    }
})

const emit = defineEmits(['starClick', 'watchReleasedSceneClick'])

const percentage = computed(() => (props.videoData.likes / props.videoData.star_goal) * 100)
const isReleased = computed(() => props.videoData.release_status === 2 && props.videoData.scene_slug)
const isProduction = computed(() => props.videoData.release_status === 1)
const isFunded = computed(() => props.videoData.star_goal_reached)

const showSponsoredIndicators = computed(() => props.isUserLoggedIn && props.videoData.sponsored_by_user)

const creatorInitial = computed(() => props.videoData.creator.name?.[0] || '?')

// Circle progress calculations
const radius = 28
const circumference = 2 * Math.PI * radius

const strokeDashoffset = computed(() => {
    const clampedPercentage = Math.min(percentage.value, 100)
    return circumference - (clampedPercentage / 100) * circumference
})

const starAriaLabel = computed(() => {
    return showSponsoredIndicators.value
        ? `You already added ${props.videoData.stars_given_by_user} stars. Give more.`
        : 'Give stars'
})

const progressStroke = computed(() => {
    return isFunded.value
        ? 'rgba(0, 81, 213, 0.9)' // Deep Blue 90%
        : 'rgba(0, 100, 250, 0.9)' // Brand Blue 90%
})

const ringBgStroke = computed(() => {
    if (isFunded.value) return 'rgba(0, 81, 213, 0.2)' // Deep Blue tint
    if (showSponsoredIndicators.value) return 'rgba(0, 100, 250, 0.2)' // Brand Blue tint
    return 'rgba(255, 255, 255, 0.2)' // White default
})

const handleImageError = (e) => {
    e.target.style.display = 'none'
    e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-white text-xl font-bold">${creatorInitial.value}</div>`
}
</script>
